import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import * as cartService from '../services/cartService';
import { CART_KEY, CART_PREVIEWS_KEY, PENDING_CART_ITEM_KEY } from '../constants/storageKeys';
import { normalizeImagePath } from '../utils/imageUtils';

const CartContext = createContext();

// Helper to get item signature for matching
const getItemSignature = (item) => {
  const baseName = item.baseName || item.configuration?.baseName || '';
  const shadeName = item.shadeName || item.configuration?.shadeName || '';
  const baseColor = item.baseColor || item.configuration?.baseColor || '';
  const shadeColor = item.shadeColor || item.configuration?.shadeColor || '';
  return `${baseName}-${shadeName}-${baseColor}-${shadeColor}`;
};

// Helper to load preview data from localStorage
const loadPreviewData = () => {
  try {
    const stored = localStorage.getItem(CART_PREVIEWS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (e) {
    return {};
  }
};

// Helper to save preview data to localStorage
const savePreviewData = (data) => {
  try {
    localStorage.setItem(CART_PREVIEWS_KEY, JSON.stringify(data));
  } catch (e) {
    console.error('[Cart] Error saving preview data:', e);
  }
};

// Helper to load from localStorage
const loadCartFromStorage = () => {
  try {
    const stored = localStorage.getItem(CART_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      console.log('[Cart] Loaded from localStorage:', parsed.length, 'items');
      return parsed;
    }
  } catch (e) {
    console.error('[Cart] Error loading from localStorage:', e);
  }
  return [];
};

// Helper to save to localStorage
const saveCartToStorage = (items) => {
  try {
    localStorage.setItem(CART_KEY, JSON.stringify(items));
    console.log('[Cart] Saved to localStorage:', items.length, 'items');
  } catch (e) {
    console.error('[Cart] Error saving to localStorage:', e);
  }
};

export function CartProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState(loadCartFromStorage);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isInitialized = useRef(false);

  // Save to localStorage whenever items change (after first render)
  useEffect(() => {
    if (isInitialized.current) {
      saveCartToStorage(items);
    } else {
      isInitialized.current = true;
    }
  }, [items]);

  // Fetch cart from API for authenticated users
  const fetchApiCart = useCallback(async () => {
    if (!isAuthenticated) return [];

    try {
      console.log('[Cart] Fetching from API...');
      const response = await cartService.getCart();
      const savedPreviews = loadPreviewData();

      if (response.success && response.data && response.data.items) {
        console.log('[Cart] API returned:', response.data.items.length, 'items');
        return response.data.items.map(item => {
          const baseItem = {
            id: item.id,
            productVariantId: item.productVariantId,
            productVariantSku: item.productVariantSku,
            productVariantName: item.productVariantName,
            productName: item.productName,
            productCategoryCode: item.productCategoryCode,
            quantity: item.quantity,
            price: parseFloat(item.priceAtAddition) || 0,
            currentPrice: parseFloat(item.currentPrice) || 0,
            priceChanged: item.priceChanged,
            subtotal: parseFloat(item.subtotal) || 0,
            configuration: item.configuration,
            baseName: item.configuration?.baseName || item.productVariantName?.split(' × ')[0] || 'Custom',
            shadeName: item.configuration?.shadeName || item.productVariantName?.split(' × ')[1]?.split(' in ')[0] || 'Custom',
            baseColorName: item.configuration?.baseColorName || '',
            shadeColorName: item.configuration?.shadeColorName || '',
            baseId: item.configuration?.baseId,
            shadeId: item.configuration?.shadeId,
            baseColor: item.configuration?.baseColor,
            shadeColor: item.configuration?.shadeColor,
            modelCode: item.productVariantSku || '',
            previewImage: normalizeImagePath(item.configuration?.thumbnailUrl, item.configuration?.baseId),
            isApiItem: true,
          };

          // Merge saved preview data if available
          const signature = getItemSignature(baseItem);
          const preview = savedPreviews[signature];
          if (preview) {
            return { ...baseItem, ...preview };
          }
          return baseItem;
        });
      }
      return [];
    } catch (err) {
      console.error('[Cart] API fetch error:', err);
      return [];
    }
  }, [isAuthenticated]);

  // Sync cart when authentication changes
  useEffect(() => {
    const syncCart = async () => {
      if (isAuthenticated) {
        setLoading(true);
        try {
          // Fetch API cart
          const apiItems = await fetchApiCart();

          // Replace items with API items (they are the source of truth for logged-in users)
          setItems(apiItems);

          // Handle pending cart item after login
          const pendingItem = sessionStorage.getItem(PENDING_CART_ITEM_KEY);
          if (pendingItem) {
            try {
              const cartItem = JSON.parse(pendingItem);
              console.log('[Cart] Adding pending item from login:', cartItem);
              await addToCartApi(cartItem);
              sessionStorage.removeItem(PENDING_CART_ITEM_KEY);
            } catch (e) {
              console.error('[Cart] Error adding pending item:', e);
            }
          }
        } catch (err) {
          console.error('[Cart] Error syncing cart:', err);
        } finally {
          setLoading(false);
        }
      } else {
        // Clear cart when user logs out
        setItems([]);
      }
    };
    
    syncCart();
  }, [isAuthenticated, fetchApiCart]);

  /**
   * Add custom lamp configuration to cart via API
   * Creates product variant on-the-fly if it doesn't exist
   */
  const addToCartApi = async (item) => {
    console.log('[Cart] Adding to cart via API:', item);
    setLoading(true);
    setError(null);

    // Save preview data to localStorage for persistence
    const previewData = {
      baseCode: item.baseCode,
      shadeCode: item.shadeCode,
      baseColorHex: item.baseColorHex,
      shadeColorHex: item.shadeColorHex,
      previewImage: item.previewImage,
    };
    const signature = getItemSignature(item);
    const allPreviews = loadPreviewData();
    allPreviews[signature] = previewData;
    savePreviewData(allPreviews);

    try {
      const response = await cartService.addCustomToCart({
        baseId: String(item.baseId),
        shadeId: String(item.shadeId),
        baseColor: item.baseColor,
        shadeColor: item.shadeColor,
        baseName: item.baseName,
        shadeName: item.shadeName,
        baseColorName: item.baseColorName,
        shadeColorName: item.shadeColorName,
        quantity: 1
      });

      console.log('[Cart] API response:', response);

      if (response.success) {
        // Refresh cart from API to get updated items
        const updatedItems = await fetchApiCart();

        // Merge preview data back into the newly added item
        const mergedItems = updatedItems.map(apiItem => {
          // Match by base/shade/color combination
          if (apiItem.configuration?.baseColor === item.baseColor &&
              apiItem.configuration?.shadeColor === item.shadeColor &&
              apiItem.baseName === item.baseName &&
              apiItem.shadeName === item.shadeName) {
            return { ...apiItem, ...previewData };
          }
          return apiItem;
        });

        setItems(mergedItems);
        return response.data;
      }
      throw new Error(response.message || 'Failed to add to cart');
    } catch (err) {
      console.error('[Cart] API error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to add to cart');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Add item to cart (local storage only - for guests)
   */
  const addToCartLocal = useCallback((item) => {
    const cartItem = {
      id: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      baseId: item.baseId,
      shadeId: item.shadeId,
      baseCode: item.baseCode,
      shadeCode: item.shadeCode,
      baseColor: item.baseColor,
      shadeColor: item.shadeColor,
      baseColorHex: item.baseColorHex,
      shadeColorHex: item.shadeColorHex,
      baseName: item.baseName,
      shadeName: item.shadeName,
      baseColorName: item.baseColorName,
      shadeColorName: item.shadeColorName,
      modelCode: item.modelCode,
      modelName: item.modelName,
      quantity: 1,
      price: item.price || 1000,
      previewImage: item.previewImage,
      createdAt: new Date().toISOString(),
      isApiItem: false,
    };

    setItems((prevItems) => {
      const existingIndex = prevItems.findIndex(
        (i) =>
          i.baseId === cartItem.baseId &&
          i.shadeId === cartItem.shadeId &&
          i.baseColor === cartItem.baseColor &&
          i.shadeColor === cartItem.shadeColor
      );

      if (existingIndex >= 0) {
        const updated = [...prevItems];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + 1
        };
        console.log('[Cart] Incremented quantity for existing item');
        return updated;
      } else {
        console.log('[Cart] Added new item:', cartItem.modelName);
        return [...prevItems, cartItem];
      }
    });
  }, []);

  /**
   * Main addToCart function
   * Uses API for authenticated users, localStorage for guests
   */
  const addToCart = useCallback(async (item) => {
    console.log('[Cart] addToCart called, isAuthenticated:', isAuthenticated);
    
    if (isAuthenticated) {
      // For authenticated users, use API
      try {
        await addToCartApi(item);
      } catch (err) {
        // Fallback to local storage if API fails
        console.error('[Cart] API failed, falling back to local:', err);
        addToCartLocal(item);
      }
    } else {
      // For guests, use local storage
      addToCartLocal(item);
    }
  }, [isAuthenticated, addToCartLocal]);

  // Remove from cart
  const removeFromCart = async (itemId) => {
    console.log('[Cart] Removing item:', itemId);
    const item = items.find(i => i.id === itemId);
    
    if (item?.isApiItem && isAuthenticated) {
      try {
        setLoading(true);
        await cartService.removeCartItem(itemId);
        const updatedItems = await fetchApiCart();
        setItems(updatedItems);
      } catch (err) {
        console.error('[Cart] Error removing API item:', err);
        setItems((prev) => prev.filter((i) => i.id !== itemId));
      } finally {
        setLoading(false);
      }
    } else {
      setItems((prev) => prev.filter((i) => i.id !== itemId));
    }
  };

  // Update quantity
  const updateQuantity = async (itemId, newQuantity) => {
    console.log('[Cart] Updating quantity:', itemId, newQuantity);
    
    if (newQuantity <= 0) {
      await removeFromCart(itemId);
      return;
    }

    const item = items.find(i => i.id === itemId);

    if (item?.isApiItem && isAuthenticated) {
      try {
        setLoading(true);
        await cartService.updateCartItem(itemId, newQuantity);
        const updatedItems = await fetchApiCart();
        setItems(updatedItems);
      } catch (err) {
        console.error('[Cart] Error updating API item:', err);
        setItems((prev) =>
          prev.map((i) => i.id === itemId ? { ...i, quantity: newQuantity } : i)
        );
      } finally {
        setLoading(false);
      }
    } else {
      setItems((prev) =>
        prev.map((i) => i.id === itemId ? { ...i, quantity: newQuantity } : i)
      );
    }
  };

  // Clear cart
  const clearCart = async () => {
    console.log('[Cart] Clearing cart');
    
    if (isAuthenticated) {
      try {
        setLoading(true);
        await cartService.clearCart();
      } catch (err) {
        console.error('[Cart] Error clearing API cart:', err);
      } finally {
        setLoading(false);
      }
    }
    
    setItems([]);
    localStorage.removeItem(CART_KEY);
  };

  const getTotalItems = () => {
    return items.reduce((sum, item) => sum + (item.quantity || 0), 0);
  };

  const getTotalPrice = () => {
    return items.reduce((sum, item) => {
      const price = parseFloat(item.price) || 0;
      const quantity = parseInt(item.quantity) || 0;
      return sum + (price * quantity);
    }, 0);
  };

  const value = {
    items,
    loading,
    error,
    addToCart,
    addToCartApi,
    removeFromCart,
    updateQuantity,
    clearCart,
    getTotalItems,
    getTotalPrice,
    refreshCart: fetchApiCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
